/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppBar, Breadcrumbs, Icon, IconButton, Toolbar, Typography } from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import CustomTooltip from '../tooltip/Tooltip';
import PillButton from '../button/PillButton';
import UserAvatar from '../display/UserAvatar';

export interface INavBarProps {
    env : string;
    version : string;
    currentUser : any;
    signOut : () => void;
    openDrawer : () => void;
    closeDrawer : () => void;
    openUserSettings : () => void;
    closeUserSettings : () => void;
    userSettingsDialog : React.ReactElement | React.ReactElement<any, string | React.JSXElementConstructor<any>>;

    breadcrumbs : Array<{
        name : string;
        url : string;
    }>;

    updateVersion ?: () => void;
    headings ?: Array<string>;
    isLatestVersion ?: boolean;
    disabled ?: boolean;
    disabledTooltip ?: string;
}

/**
 * @param {string} env - The current environment (e.g., 'development', 'production'). Used to conditionally render or alter behavior based on environment. Required.
 * @param {string} version - The current version of the application. Required.
 * @param {any} currentUser - The current user's information. This can be any type that represents user data. Required.
 * @param {() => void} signOut - Callback function to sign out the current user. Required.
 * @param {() => void} openDrawer - Callback function to open the navigation drawer. Required.
 * @param {() => void} closeDrawer - Callback function to close the navigation drawer. Required.
 * @param {() => void} openUserSettings - Callback function to open the user settings dialog. Required.
 * @param {() => void} closeUserSettings - Callback function to close the user settings dialog. Required.
 * @param {React.ReactElement | React.ReactElement<any, string | React.JSXElementConstructor<any>>} userSettingsDialog - React element for displaying the user settings dialog. This could be a modal or another type of UI component. Required.
 * @param {Array<{name: string, url: string}>} breadcrumbs - Array of objects representing the navigation breadcrumbs, each containing a 'name' and a 'url'. Used for navigation and indicating the current path. Required.
 * @param {() => void} [updateVersion] - Optional callback function to update the application to the latest version.
 * @param {Array<string>} [headings] - Optional array of strings representing additional headings or titles in the navigation bar.
 * @param {boolean} [isLatestVersion] - Optional flag indicating whether the current version is the latest. This could affect the display of update notifications or prompts.
 * @param {boolean} [disabled] - Optional flag to disable the navigation bar, possibly used during loading states or other conditions where user interaction should be limited.
 * @param {string} [disabledTooltip] - Optional tooltip message displayed when the navigation bar is disabled, providing users with information on why it is disabled.
 * 
 * @return {React.ReactElement} The React element for the NavBar component.
 */
const NavBar = (props : INavBarProps) : JSX.Element => {

    /*================================================================================================================
     *                                                       Memos
     * ==============================================================================================================*/

    const menuTooltipMessage = useMemo<string>(() => {
        if (props.disabled) {
            if (props.disabledTooltip) {
                return props.disabledTooltip;
            }
            return 'Menu currently disabled';
        }

        return 'Menu';
    }, [props.disabled, props.disabledTooltip]);

    const homeTooltipMessage = useMemo<string>(() => {
        if (props.disabled) {
            if (props.disabledTooltip) {
                return props.disabledTooltip;
            }
            return 'Home currently disabled';
        }

        return 'Home';
    }, [props.disabled, props.disabledTooltip]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <AppBar position='static' className={'wsnw mxh52 h52'} elevation={0}>
            <Toolbar variant='dense' >
                <div className={'flx1 jcfs'}>
                    <CustomTooltip title={menuTooltipMessage}>
                        <IconButton color={'inherit'} disabled={props.disabled} className={'fdc jcc mr20'} onMouseEnter={props.openDrawer} onClick={props.openDrawer}>
                            <Icon className='cw'>menu</Icon>
                        </IconButton>
                    </CustomTooltip>
                    <CustomTooltip title={homeTooltipMessage} className={'fdc aic jcc'}>
                        <Link to={'/home'} onClick={props.closeDrawer} style={{ textDecoration: 'unset', color: 'unset' }}>
                            <img height={42} src={`${ASSET_BASE}/assets/images/icons/${ICONS.project_logo}`} className={'mr10 cursorPointer'} />
                        </Link>
                    </CustomTooltip>
                    <Typography color='inherit' variant='h6' className={'asc mr30'}>
                        {props.env}
                    </Typography>
                    <div className={'fdc jcc pt5 mr30'}>
                        <Breadcrumbs separator={'>'} className={'fdr cs fs14 aife'}>
                            {
                                props.breadcrumbs.map((x) => {
                                    if (x.url) {
                                        return (
                                            <div key={`breadcrumb_${x.url}_${x.name}_outer`} className='fdc jcc'>
                                                <Link color='inherit' to={x.url} key={`breadcrumb_${x.url}_${x.name}`} className={'breadcrumbLink'}>{x.name}</Link>
                                            </div>
                                        );
                                    } else {
                                        return <span color={'inherit'} key={`breadcrumb_${x.url}_${x.name}`}>{x.name}</span>;
                                    }
                                })
                            }
                        </Breadcrumbs>
                    </div>
                </div>
                <div className={'flx1 jcfs'}>
                    <div className={'flx1 fdr aic jcfe'}>
                        {
                            props.headings?.map(heading =>                 
                                <Typography key={uuidv4()} className={'cs mr20 fs14 pt5'} color='inherit'>
                                    {heading}
                                </Typography>
                            )
                        }
                    </div>
                    <div className={'ml20 mr20'} />
                    <Typography color='inherit' className={'fdc aic jcc pt5'}>
                        {props.currentUser?.name}
                    </Typography>
                    <div className={'ml10 mr10'} />
                    <CustomTooltip title={'User Settings'} className={'fdc jcc'}>
                        <div>
                            <IconButton color={'inherit'} className={'mr10'} onClick={props.openUserSettings}>
                                <UserAvatar currentUser={props.currentUser}/>
                                <Icon className={'cw posa post30 posl40'}>account_circle</Icon>
                            </IconButton>
                        </div>
                    </CustomTooltip>
                    <div className={'ml5 mr5'} />
                    <div className={'fdc jcc'}>
                        {
                            !props.isLatestVersion && props.updateVersion &&
                            <PillButton
                                color={'secondary'}
                                onClick={props.updateVersion}
                                className={'fdr aic jcc h35 m10'}
                                text={'Update'}
                                icon={<CloudUploadIcon className='jcc'/>}
                            ></PillButton>
                        }
                    </div>
                    <sub className={'fdc aic jcc pt5'}>v{props.version}</sub>
                </div>
            </Toolbar>
            {
                props.userSettingsDialog
            }
        </AppBar>
    );
};

export default NavBar;
